import { useState, useRef, useEffect, useCallback, useMemo } from "react";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { usePermission } from "../../../../hooks/usePermission";
import { Modal, ModalHeader } from "../../../../globalComponents/ui/Modal";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config";
import { PrimaryButton, SecondaryButton } from "../../../../globalComponents/ui/Button";
import SimpleDropdown from "../../../../globalComponents/form/dropdowns/SimpleDropdown";
import KpiFormFields from "./KpiFormFields";
import MultiDocumentUploadField from "../../components/MultiDocumentUploadField";
import { mergeKpiAndAssetUnits } from "./assetUnits";
import { defaultPeriodForFrequency, reportablePeriodOptions } from "../helpers";
import { useEffectiveFrequency } from "../hooks/useEffectiveFrequency";

const today = new Date().toISOString().split("T")[0];

const ALL_CALC_METHODS = [
  { id: "SPEND_BASED", label: "Spend Based", short: "Spend", icon: "💰", color: "#7c3aed", bg: "#f5f3ff", border: "#ddd6fe" },
  { id: "ACTIVITY_BASED", label: "Activity Based", short: "Activity", icon: "⚙️", color: "#0369a1", bg: "#eff6ff", border: "#bfdbfe" },
];

const EMISSION_KPI_TYPES = ["EMISSION", "EMISSION_ENERGY"];

/** Caption under the period picker, naming the cadence the options come from. */
const FREQUENCY_CAPTIONS = {
  MONTHLY: "MONTHLY — COMPLETED MONTHS ONLY",
  QUARTERLY: "QUARTERLY — COMPLETED QUARTERS ONLY",
  HALF_YEARLY: "HALF YEARLY — COMPLETED HALVES ONLY",
  YEARLY: "YEARLY",
};

/**
 * Resolves a location option into the scope it represents. Options are one per
 * reportable scope: a whole site, or a single sub-location keyed `sub_<id>`.
 * A split site is only ever offered as its sub-locations.
 *
 * `locationId` on the option is the only source of truth for the parent site —
 * every option built by `fetchLocations` carries it. A `sub_<id>` key without
 * one has no resolvable parent, so it yields `locationId: null`; callers must
 * reject that scope instead of persisting a record with no location.
 */
const scopeOfOption = (option) => {
  if (option?.locationId) {
    return { locationId: Number(option.locationId), subLocationId: option.subLocationId ?? null };
  }
  const raw = String(option?.id ?? "");
  if (raw.startsWith("sub_")) {
    return { locationId: null, subLocationId: Number(raw.slice(4)) };
  }
  return { locationId: Number(raw) || null, subLocationId: null };
};

const extractAssetRows = (res) => {
  const raw = res?.data?.data ?? res?.data ?? res;
  return Array.isArray(raw) ? raw : [];
};

const normalizeAsset = (asset) => ({
  ...asset,
  id: asset.id,
  kpiId: asset.kpiId ?? asset.kpi_id,
  sourceId: asset.sourceId ?? asset.source_id ?? null,
  subLocationId: asset.subLocationId ?? asset.sub_location_id ?? null,
  assetTitle: asset.assetTitle ?? asset.asset_title ?? asset.title ?? "",
  isLocationRequired:
    (asset.isLocationRequired ?? asset.is_location_required ?? true) ? true : false,
  supportedUnits: Array.isArray(asset.supportedUnits ?? asset.supported_units)
    ? (asset.supportedUnits ?? asset.supported_units)
    : null,
});

const FieldLabel = ({ children, required }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: "var(--ink-3)", textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", marginBottom: 6 }}>
    {children}
    {required && <span style={{ color: "var(--red)", marginLeft: 2 }}>*</span>}
  </div>
);

// ── Required Evidences banner ─────────────────────────────────────────────────
function RequiredEvidences({ requiredEvidences }) {
  if (!requiredEvidences || requiredEvidences.length === 0) return <></>;

  return (
    <div style={{
      margin: "0 0 0 0",
      background: "linear-gradient(135deg, #3f88a5 0%, #357a96 100%)",
      padding: "12px 24px 14px",
      borderBottom: "1px solid #0284c7",
    }}>
      {/* Label row */}
      <div style={{ display: "flex", alignItems: "center", gap: 7, marginBottom: 10 }}>
        <span style={{ fontSize: 14 }}>📋</span>
        <span style={{ fontSize: 14, fontWeight: 800, color: "#fff", letterSpacing: "0.02em" }}>
          Required Evidences
        </span>
        <span style={{
          fontSize: 11, fontWeight: 700,
          padding: "1px 8px", borderRadius: 20,
          background: "rgba(255,255,255,0.3)", color: "#fff",
          marginLeft: 2,
        }}>
          {requiredEvidences.length}
        </span>
        <span style={{ fontSize: 14, color: "rgba(255,255,255,0.85)", marginLeft: "auto" }}>
          ensure your document covers these
        </span>
      </div>

      {/* Evidence pills */}
      <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
        {requiredEvidences.map((ev, i) => (
          <div key={i} style={{
            display: "inline-flex", alignItems: "center", gap: 7,
            background: "rgba(255,255,255,0.2)",
            border: "1px solid rgba(255,255,255,0.45)",
            borderRadius: 8, padding: "6px 12px",
            backdropFilter: "blur(4px)",
          }}>
            <span style={{
              width: 18, height: 18, borderRadius: "50%", flexShrink: 0,
              background: "#fff",
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: 10, fontWeight: 800, color: "#0369a1",
            }}>
              {i + 1}
            </span>
            <span style={{ fontSize: 13, fontWeight: 600, color: "#fff", lineHeight: 1.3 }}>{ev}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Shared Usage Distribution ─────────────────────────────────────────────────
const SharedUsageDistribution = ({ locationId, locations, ownershipControlMap }) => {
  if (!locationId) return null;
  const loc = locations.find(l => l.id === locationId);
  const control = ownershipControlMap[locationId];
  const pct = control?.percentage ?? null;
  const hasData = pct != null;

  const getBarColor = () => {
    if (!hasData) return "#cbd5e1";
    if (pct >= 50) return "#1D9E75";
    if (pct >= 20) return "#378ADD";
    return "#EF9F27";
  };

  const getBadgeStyle = () => {
    if (!hasData) return { color: "#94a3b8", background: "#f1f5f9" };
    if (pct >= 50) return { color: "#0F6E56", background: "#E1F5EE" };
    if (pct >= 20) return { color: "#185FA5", background: "#E6F1FB" };
    return { color: "#854F0B", background: "#FAEEDA" };
  };

  return (
    <div style={{ background: "#f8fafc", border: "1.5px solid #bae6fd", borderRadius: 12, padding: "12px 14px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
        <span style={{ fontSize: 13 }}>📊</span>
        <span style={{ fontSize: 12, fontWeight: 700, color: "#0369a1" }}>Shared Usage</span>
      </div>
      <div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 5 }}>
          <span style={{ fontSize: 13, fontWeight: 600, color: "var(--ink)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", maxWidth: "70%" }}>
            {loc?.name || "Location"}
          </span>
          <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 9px", borderRadius: 20, flexShrink: 0, ...getBadgeStyle() }}>
            {hasData ? `${pct}%` : "No data"}
          </span>
        </div>
        <div style={{ height: 7, background: "var(--rule)", borderRadius: 4, overflow: "hidden" }}>
          <div style={{ height: "100%", width: hasData ? `${pct}%` : "0%", background: getBarColor(), borderRadius: 4, transition: "width 0.4s ease" }} />
        </div>
        {!hasData && (
          <div style={{ fontSize: 11, color: "var(--ink-4)", marginTop: 4 }}>ℹ No ownership record found</div>
        )}
      </div>
    </div>
  );
};

const EmissionMethodPicker = ({ availableMethods, selected, onChange, categoryName, scopeName }) => {
  const methods = ALL_CALC_METHODS.filter(m =>
    availableMethods.length === 0 || availableMethods.includes(m.id)
  );
  return (
    <div>
      <FieldLabel required>Calculation Method</FieldLabel>
      {categoryName && (
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, padding: "6px 10px", background: "#f0faf5", border: "1px solid #a5dfc3", borderRadius: 7 }}>
          <span style={{ fontSize: 12 }}>🌍</span>
          <div>
            <span style={{ fontSize: 11, fontWeight: 700, color: "#1baa6f" }}>{scopeName}</span>
            <span style={{ fontSize: 11, color: "#475569" }}> → {categoryName}</span>
          </div>
          {availableMethods.length > 0 && availableMethods.length < ALL_CALC_METHODS.length && (
            <span style={{ marginLeft: "auto", fontSize: 10, color: "#64748b", background: "#e8f5e9", borderRadius: 4, padding: "1px 7px", fontWeight: 600 }}>
              {availableMethods.length} method{availableMethods.length > 1 ? "s" : ""} configured
            </span>
          )}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {methods.map(m => {
          const isActive = selected === m.id;
          return (
            <button key={m.id} type="button" onClick={() => onChange(m.id)} style={{ display: "inline-flex", alignItems: "center", gap: 6, padding: "9px 18px", borderRadius: 10, fontSize: 12, fontWeight: isActive ? 700 : 500, border: `2px solid ${isActive ? m.color : "#e2e8f0"}`, background: isActive ? m.bg : "#fafbfc", color: isActive ? m.color : "#64748b", cursor: "pointer", transition: "all 0.15s", fontFamily: "inherit", boxShadow: isActive ? `0 0 0 3px ${m.color}22` : "none", flex: methods.length <= 2 ? 1 : "none" }}>
              <span style={{ fontSize: 16 }}>{m.icon}</span>
              <div style={{ textAlign: "left" }}>
                <div>{m.label}</div>
                <div style={{ fontSize: 10, opacity: 0.7, fontWeight: 400 }}>{m.id === "SPEND_BASED" ? "Cost / financial data" : "Physical activity data"}</div>
              </div>
              {isActive && <span style={{ marginLeft: "auto", fontSize: 13, color: m.color, flexShrink: 0 }}>✓</span>}
            </button>
          );
        })}
      </div>
      {!selected && <div style={{ fontSize: 11, color: "var(--red)", marginTop: 6, fontWeight: 600 }}>⚠ Select a calculation method to continue</div>}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
════════════════════════════════════════════════════════════ */
const AddDataModal = ({ show, onHide, item, financialYearId, fyLabel = "", startingMonth = null, locations = [], onSave, saveAnswer }) => {
  const [step, setStep] = useState("choose");
  const { hasPermission, userCapabilities } = usePermission();

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [submitOnConfirm, setSubmitOnConfirm] = useState(false);
  const [sendToAuditOnConfirm, setSendToAuditOnConfirm] = useState(false);
  const [extraUploadedDocuments, setExtraUploadedDocuments] = useState([]);
  // True while MultiDocumentUploadField is still uploading a newly-picked file —
  // Save must stay disabled, otherwise it can fire before the file has a
  // fileMetadataId and silently drop that attachment.
  const [docsUploading, setDocsUploading] = useState(false);
  // When false, uploaded document(s) are never sent for AI parsing — the backend
  // marks them isVerified immediately instead, trusting the manually-entered value.
  const [shouldParse, setShouldParse] = useState(true);
  const [conflictPayload, setConflictPayload] = useState(null);
  const [locSearch, setLocSearch] = useState("");

  const [configuredMethods, setConfiguredMethods] = useState([]);
  const [emissionSettingsLoaded, setEmissionSettingsLoaded] = useState(false);
  const [selectedCalcMethod, setSelectedCalcMethod] = useState(null);
  const [ownershipControlMap, setOwnershipControlMap] = useState({});

  const [kpiAssets, setKpiAssets] = useState([]);
  const [assetsLoading, setAssetsLoading] = useState(false);

  // KPI form schemas & data (one entry per kpi_type that has a schema)
  const [kpiFormSchemas, setKpiFormSchemas] = useState([]);   // [{ kpi, fields }, ...]
  const [kpiFormData, setKpiFormData] = useState({});         // { [kpi]: { [fieldKey]: value | { value, unit } } }
  const [schemaLoading, setSchemaLoading] = useState(false);
  const [unitMaster, setUnitMaster] = useState([]);           // [{ id, unit, unit_name }]

  const { currentUserId, currentUser, allSources } = useCurrentUser();

  // Admin determined by authorization module (userCapabilities), not role_id
  const isAdmin = userCapabilities.includes("*") ||
    (currentUserId && Number(currentUserId) === 1) ||
    (currentUser && currentUser.parent_id === null);


  const allowedLocations = useMemo(() => {
    // A split site is not a reportable scope — its sub-locations are. It stays
    // in `locations` only so existing records resolve to a name.
    const reportable = locations.filter(l => l.assignable !== false);

    // Admin (as defined by authorization) sees all locations — no filtering
    if (isAdmin) return reportable;

    // Non-admin: start with globally assigned locations (allSources).
    // allSources holds parent location ids, so a sub-location option is matched
    // through its parent — Number("sub_5") would be NaN and drop it entirely.
    let baseList = reportable;
    if (Array.isArray(allSources) && allSources.length > 0) {
      baseList = reportable.filter(l => allSources.includes(scopeOfOption(l).locationId));
    }

    // Further narrow to this KPI's specific assigned scope
    if (!item?.assign_raw_list || !currentUserId) return baseList;

    const assignedScopeKeys = new Set();  // exact sub-location grants (KMC-T1)
    const assignedWholeSites = new Set(); // site grants, which cover every sub-location
    let hasAssignmentsForUser = false;

    item.assign_raw_list.forEach(a => {
      const assignee = (a.assignees || []).find(x => String(x.userId) === String(currentUserId));
      const userIds = (a.assignedTo || a.assigned_to || []).map(String);
      if (!assignee && !userIds.includes(String(currentUserId))) return;

      hasAssignmentsForUser = true;

      // `scopes` distinguishes KMC-T1 from the whole site; older payloads carry
      // only location ids, which can only mean a whole-site assignment.
      if (assignee?.scopes?.length) {
        assignee.scopes.forEach(s => {
          if (s.subLocationId) assignedScopeKeys.add(`sub_${s.subLocationId}`);
          else if (s.locationId != null) assignedWholeSites.add(String(s.locationId));
        });
        return;
      }

      const sIds = assignee?.locationIds || a.sourceIds || a.source_ids || [];
      sIds.forEach(id => {
        if (id !== null && id !== undefined) assignedWholeSites.add(String(id));
      });
    });

    // No KPI-specific assignment for this user — fall back to globally assigned locations
    if (!hasAssignmentsForUser) return baseList;

    return baseList.filter(l => {
      const { locationId, subLocationId } = scopeOfOption(l);
      if (subLocationId) {
        return assignedScopeKeys.has(`sub_${subLocationId}`) || assignedWholeSites.has(String(locationId));
      }
      return assignedWholeSites.has(String(locationId));
    });
  }, [locations, item?.assign_raw_list, currentUserId, isAdmin, allSources]);

  const isEmissionKpi = EMISSION_KPI_TYPES.includes(item?.kpi_type);
  const emissionCategoryId = item?.emission_category_id ?? null;
  const emissionCategoryName = item?.emission_category?.name ?? null;
  const emissionScopeName = item?.emission_category?.scopeName ?? item?.emission_category?.scope ?? null;

  /** The KPI's supported units, memoized so it stays a stable dependency. */
  const resolvedUnitObjs = useMemo(
    () => (item?.unitSetting?.supportedUnits?.length ? item.unitSetting.supportedUnits : []),
    [item?.unitSetting?.supportedUnits]
  );
  const defaultUnit = item?.unitSetting?.selectedUnit ?? resolvedUnitObjs[0]?.unit ?? "";
  const defaultUnitId = item?.unitSetting?.selectedUnitId ?? resolvedUnitObjs.find(u => u.unit === defaultUnit)?.unit_id ?? null;

  /**
   * A KPI configured in KPI Unit Settings is reported in that unit only: the
   * picker becomes a read-only chip and the selected asset's own units are not
   * offered, so every value stored for the KPI shares one unit. The backend
   * rejects any other unit, so an editable picker would only hand the user a
   * choice the save refuses. KPIs with no configured unit keep the picker.
   */
  const isUnitLocked = item?.unitSetting?.selectedUnitId != null && !!defaultUnit;
  const [requiredEvidences, setRequiredEvidences] = useState(null);

  /**
   * Date range the KPI's configured frequency puts this submission in — Monthly
   * fills the running month, Quarterly the running quarter, and so on. Only a
   * starting point: both inputs stay editable.
   */
  const defaultPeriod = useMemo(
    () => defaultPeriodForFrequency(item?.answerFrequency, fyLabel, startingMonth),
    [item?.answerFrequency, fyLabel, startingMonth]
  );

  const [form, setForm] = useState({
    location_id: null,
    sub_location_ids: [],
    from_date: defaultPeriod?.fromDate ?? "",
    to_date: defaultPeriod?.toDate ?? "",
    value: "",
    unit: defaultUnit,
    unit_id: defaultUnitId,
    kpiAssetId: null,
    utility_type: "exclusive",
  });

  const [openLocDD, setOpenLocDD] = useState(false);
  const [openSubLocDD, setOpenSubLocDD] = useState(false);
  const locRef = useRef(null);
  const subLocRef = useRef(null);

  // Ownership config is keyed by real location id, never by a `sub_<id>` scope key.
  const selectedLocationIdForConfig = form.location_id == null
    ? null
    : scopeOfOption(locations.find(l => l.id === form.location_id) ?? { id: form.location_id }).locationId;
  const selectedLocationHasConfig = selectedLocationIdForConfig != null && !!ownershipControlMap[selectedLocationIdForConfig];

  const fetchOwnershipControls = useCallback(async () => {
    const fyId = financialYearId || item?.financial_year_id;
    if (!fyId) return;
    const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}ownershipControl`, {}, { financialYearId: fyId }, "GET");
    if (!isSuccess || !Array.isArray(data)) return;
    const map = {};
    data.forEach(rec => {
      if (rec.isDisabled || rec.toDate) return;
      map[rec.locationId] = {
        percentage: rec.totalValue && rec.occupiedValue
          ? Number(((rec.occupiedValue / rec.totalValue) * 100).toFixed(2))
          : 0,
        ownership: rec,
      };
    });
    setOwnershipControlMap(map);
  }, [financialYearId, item]);

  const fetchEmissionSettings = useCallback(async () => {
    if (!isEmissionKpi || !emissionCategoryId) {
      setConfiguredMethods([]);
      setEmissionSettingsLoaded(true);
      return;
    }
    try {
      const { isSuccess, data } = await apiCall(`${config.POSTLOGIN_API_URL_COMPANY}emissionKpiSettings`, {}, {}, "GET");
      if (isSuccess) {
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        const row = list.find(r => r.emission_category_id === emissionCategoryId && r.is_enabled);
        if (row?.calculation_methods?.length > 0) {
          setConfiguredMethods(row.calculation_methods);
          if (row.calculation_methods.length === 1) setSelectedCalcMethod(row.calculation_methods[0]);
        } else {
          setConfiguredMethods([]);
        }
      }
    } catch {
      setConfiguredMethods([]);
    } finally {
      setEmissionSettingsLoaded(true);
    }
  }, [isEmissionKpi, emissionCategoryId]);

  const fetchKpiAssets = useCallback(async () => {
    const fyId = financialYearId || item?.financial_year_id;
    const questionId = item?.id;
    if (!fyId || !questionId) {
      setKpiAssets([]);
      return;
    }


    setAssetsLoading(true);
    try {
      const res = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}kpiAssetSettings`,
        {},
        { fyid: fyId },
        "GET"
      );
      const assets = extractAssetRows(res)
        .map(normalizeAsset)
        .filter(a => String(a.kpiId) === String(questionId) && a.id && a.assetTitle);
      setKpiAssets(assets);
    } catch {
      setKpiAssets([]);
    } finally {
      setAssetsLoading(false);
    }
  }, [financialYearId, item]);

  // ── Fetch unit master (id → unit string) ─────────────────────────────────
  const fetchUnitMaster = useCallback(async () => {
    try {
      const { isSuccess, data } = await apiCall(
        `${config.POSTLOGIN_API_URL_COMPANY}getUnitMaster`,
        {}, {}, "GET", false, false
      );
      if (isSuccess) {
        const list = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];
        setUnitMaster(list);
      }
    } catch {
      setUnitMaster([]);
    }
  }, []);

  // ── Fetch KPI form schemas (one per kpi_type) ────────────────────────────
  const fetchKpiFormSchema = useCallback(async () => {
    // Normalise kpi_type to an array (API may return string or array)
    const rawType = item?.kpi_type;
    const types = Array.isArray(rawType)
      ? rawType
      : rawType
        ? [rawType]
        : [];

    if (!types.length) {
      setKpiFormSchemas([]);
      return;
    }

    setSchemaLoading(true);
    try {
      // Fetch all types in parallel; silently ignore 404s / disabled schemas
      const results = await Promise.allSettled(
        types.map((kpi) =>
          apiCall(
            `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/kpi-form-schema/${kpi}`,
            {},
            {},
            "GET",
            false,  // no success icon
            false   // no error sweetAlert (404 is expected for types without a schema)
          )
        )
      );

      const schemas = [];
      results.forEach((result) => {
        if (result.status !== "fulfilled") return;
        const { isSuccess, data } = result.value ?? {};
        // Backend returns { success, data: { kpi, form, isEnabled, ... } }
        const entity = data?.data ?? data;
        if (isSuccess && entity?.isEnabled && Array.isArray(entity?.form)) {
          schemas.push({ kpi: entity.kpi, fields: entity.form });
        }
      });

      setKpiFormSchemas(schemas);
    } catch {
      setKpiFormSchemas([]);
    } finally {
      setSchemaLoading(false);
    }
  }, [item]);


  const fetchRequiredEvidences = useCallback(async () => {
    if (!item?.id || !financialYearId) {
      setRequiredEvidences([]);
      return;
    }

    apiCall(
      `${config.POSTLOGIN_API_URL_COMPANY}settings/kpis/required-evidences/${item.id}`,
      {},
      { fyid: financialYearId },
      "GET"
    )
      .then(({ isSuccess, data }) => {
        setRequiredEvidences(
          isSuccess && Array.isArray(data?.data?.requiredEvidences)
            ? data.data.requiredEvidences
            : []
        );
      })
      .catch(() => {
        setRequiredEvidences([]);
      });
  }, [item?.id, financialYearId]);

  useEffect(() => {
    if (show) {
      setStep("choose");
      setError("");
      setExtraUploadedDocuments([]);
      setShouldParse(true);
      setOpenLocDD(false); setOpenSubLocDD(false);
      setConflictPayload(null); setSelectedCalcMethod(null);
      setConfiguredMethods([]); setEmissionSettingsLoaded(false);
      setOwnershipControlMap({});
      setKpiAssets([]);
      setAssetsLoading(false);
      setKpiFormSchemas([]);
      setKpiFormData({});
      setSchemaLoading(false);
      setUnitMaster([]);
      setForm({
        location_id: null,
        sub_location_ids: [],
        from_date: defaultPeriod?.fromDate ?? "",
        to_date: defaultPeriod?.toDate ?? "",
        value: "",
        unit: defaultUnit,
        unit_id: defaultUnitId,
        utility_type: "exclusive",
        kpiAssetId: null,
      });
      fetchEmissionSettings();
      fetchOwnershipControls();
      fetchRequiredEvidences();
      fetchKpiAssets();
      fetchKpiFormSchema();
      fetchUnitMaster();
    }
  }, [
    show,
    financialYearId,
    item,
    defaultUnit,
    defaultUnitId,
    defaultPeriod,
    fetchEmissionSettings,
    fetchOwnershipControls,
    fetchRequiredEvidences,
    fetchKpiAssets,
    fetchKpiFormSchema,
    fetchUnitMaster
  ]);

  useEffect(() => {
    if (show && allowedLocations.length === 1) {
      const singleLocId = allowedLocations[0].id;
      if (form.location_id !== singleLocId) {
        setForm(prev => ({ ...prev, location_id: singleLocId }));
      }
    }
  }, [show, allowedLocations, form.location_id]);

  useEffect(() => {
    if (!kpiFormSchemas.length || !unitMaster.length) return;
    setKpiFormData((prev) => {
      let changed = false;
      const next = { ...prev };
      kpiFormSchemas.forEach((schema) => {
        schema.fields.forEach((field) => {
          const hasUnits = Array.isArray(field.supportedUnits) && field.supportedUnits.length > 0;
          if (!hasUnits) return;
          const existing = next[schema.kpi]?.[field.key];
          const alreadyHasUnit =
            existing && typeof existing === "object" && "unitId" in existing && existing.unitId;
          if (alreadyHasUnit) return;
          const baseUnitId = field.supportedUnits.find(su => su.is_base)?.unit_id;
          if (!baseUnitId) return;
          const currentVal =
            existing && typeof existing === "object" && "value" in existing
              ? (existing.value ?? "")
              : (existing ?? "");
          if (!next[schema.kpi]) next[schema.kpi] = {};
          next[schema.kpi] = {
            ...next[schema.kpi],
            [field.key]: { value: currentVal, unitId: baseUnitId },
          };
          changed = true;
        });
      });
      return changed ? next : prev;
    });
  }, [kpiFormSchemas, unitMaster]);

  useEffect(() => {
    setForm(p => ({
      ...p,
      sub_location_ids: [],
      kpiAssetId: null,
      utility_type: "exclusive",
      unit: defaultUnit,
      unit_id: defaultUnitId,
    }));
  }, [form.location_id]); // eslint-disable-line

  useEffect(() => {
    const h = e => { if (locRef.current && !locRef.current.contains(e.target)) setOpenLocDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  useEffect(() => {
    const h = e => { if (subLocRef.current && !subLocRef.current.contains(e.target)) setOpenSubLocDD(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  // ── Sub-locations for the selected location ───────────────────────────────
  const selectedLocObj = allowedLocations.find(l => l.id === form.location_id) ?? null;
  const selectedScope = selectedLocObj ? scopeOfOption(selectedLocObj) : { locationId: null, subLocationId: null };

  // Picking KMC-T1 already fixes the sub-location, so the optional picker is
  // suppressed — it would otherwise let the owner of T1 write against T2.
  const activeSubLocations = selectedScope.subLocationId
    ? []
    : (selectedLocObj?.location?.subLocation ?? []);
  const hasSubLocs = activeSubLocations.length > 0;
  const hasKpiAssets = kpiAssets.length > 0;

  // Globals (isLocationRequired === false) always show; location-scoped assets
  // are added once a location is picked.
  const assetOptionsForLocation = useMemo(() => {
    const globals = kpiAssets.filter(a => a.isLocationRequired === false);
    if (!form.location_id) return globals;
    // Assets are pinned to a location AND a sub-location, so a KMC-T1 entry
    // must not offer assets belonging to KMC-T2 or to the site as a whole.
    const locationScoped = kpiAssets.filter(
      a => a.isLocationRequired !== false &&
        String(a.sourceId) === String(selectedScope.locationId) &&
        String(a.subLocationId ?? "") === String(selectedScope.subLocationId ?? "")
    );
    return [...locationScoped, ...globals];
  }, [kpiAssets, form.location_id, selectedScope.locationId, selectedScope.subLocationId]);

  const selectedAsset = useMemo(() => {
    if (!form.kpiAssetId) return null;
    return kpiAssets.find(a => String(a.id) === String(form.kpiAssetId)) ?? null;
  }, [kpiAssets, form.kpiAssetId]);

  const currentUnitObjs = useMemo(() => {
    if (!isUnitLocked) return mergeKpiAndAssetUnits(resolvedUnitObjs, selectedAsset, unitMaster);
    const locked = resolvedUnitObjs.find(u => Number(u.unit_id) === Number(defaultUnitId));
    return [locked ?? { unit_id: defaultUnitId, unit: defaultUnit, factor: 1 }];
  }, [isUnitLocked, defaultUnit, defaultUnitId, selectedAsset, unitMaster, resolvedUnitObjs]);

  const assetKindLabel = useMemo(() => {
    if (!kpiAssets.length) return "Asset";
    return kpiAssets.every(a => a.isLocationRequired === false) ? "Global Asset" : "Asset";
  }, [kpiAssets]);

  /**
   * Cadence in force for the site being entered against. Frequency is configured
   * per location and sub-location, so the KPI list's company-wide
   * `answerFrequency` only holds until a location is picked.
   */
  const { frequency: effectiveFrequency } = useEffectiveFrequency({
    financialYearId: financialYearId || item?.financial_year_id,
    kpiId: item?.id,
    locationId: selectedScope.locationId,
    subLocationId: selectedScope.subLocationId,
    fallback: item?.answerFrequency,
    enabled: show,
  });

  /**
   * Reporting periods this KPI may be entered for — completed periods of the
   * cadence only, so a month, quarter or year still running is never offered.
   * Empty for CUSTOM and for a year with no period to derive, where the data
   * owner types a range instead.
   */
  const periodOptions = useMemo(
    () => reportablePeriodOptions(effectiveFrequency, fyLabel, startingMonth),
    [effectiveFrequency, fyLabel, startingMonth]
  );

  const selectedPeriodValue = `${form.from_date}|${form.to_date}`;

  const selectedPeriod = periodOptions.find(option => option.value === selectedPeriodValue) ?? null;

  useEffect(() => {
    if (!show || !periodOptions.length) return;
    if (periodOptions.some(option => option.value === selectedPeriodValue)) return;
    const due = periodOptions[periodOptions.length - 1];
    setForm(p => ({ ...p, from_date: due.fromDate, to_date: due.toDate }));
  }, [show, periodOptions, selectedPeriodValue]);

  /**
   * Stages the from/to range of the period the data owner picked.
   * @param {string} value the option value, carrying both boundaries.
   */
  const handlePeriodChange = (value) => {
    const picked = periodOptions.find(option => option.value === value);
    if (!picked) return;
    setForm(p => ({ ...p, from_date: picked.fromDate, to_date: picked.toDate }));
  };

  const setField = (key, val) => setForm(p => ({ ...p, [key]: val }));

  const selectLoc = (id) => {
    setForm(p => ({
      ...p,
      location_id: p.location_id === id ? null : id,
      sub_location_ids: [],
      utility_type: "exclusive",
      kpiAssetId: null,
    }));
    setOpenLocDD(false);
  };

  /**
   * Picks a KPI asset (or clears it) and snaps the unit back to the KPI's
   * default. When the KPI's unit is locked that default is the only unit
   * available; otherwise the asset's own units stay in the dropdown, just not
   * auto-selected.
   * @param {number|string|null} assetId the asset to select, or null to clear.
   */
  const selectAsset = (assetId) => {
    const asset = assetId
      ? kpiAssets.find(a => String(a.id) === String(assetId))
      : null;

    setForm(p => ({
      ...p,
      kpiAssetId: assetId ? Number(assetId) : null,
      sub_location_ids: asset?.subLocationId ? [Number(asset.subLocationId)] : p.sub_location_ids,
      unit: defaultUnit,
      unit_id: defaultUnitId,
    }));
  };

  const filteredLocations = allowedLocations.filter(l =>
    l.name?.toLowerCase().includes(locSearch.toLowerCase())
  );

  const toggleSubLoc = (id) => setForm(p => ({
    ...p,
    sub_location_ids: p.sub_location_ids.includes(id) ? p.sub_location_ids.filter(x => x !== id) : [...p.sub_location_ids, id],
  }));

  const toggleAllSubLocs = () => setForm(p => ({
    ...p,
    sub_location_ids: p.sub_location_ids.length === activeSubLocations.length ? [] : activeSubLocations.map(s => s.id),
  }));

  const handleUnitChange = (unitString) => {
    const matched = currentUnitObjs.find(u => u.unit === unitString);
    setForm(p => ({ ...p, unit: unitString, unit_id: matched?.unit_id ?? null }));
  };

  const locLabel = () => {
    if (!form.location_id) return "Select a location…";
    return allowedLocations.find(l => l.id === form.location_id)?.name ?? "Location";
  };

  const subLocLabel = () => {
    const { sub_location_ids: ids } = form;
    if (!ids.length) return "Select sub-locations…";
    if (ids.length === activeSubLocations.length) return "All sub-locations";
    const names = ids.map(id => {
      const sl = activeSubLocations.find(s => s.id === id);
      return sl ? `${selectedLocObj?.name}-${sl.subLocation}` : null;
    }).filter(Boolean);
    return names.length <= 2 ? names.join(", ") : `${names[0]}, +${names.length - 1} more`;
  };

  const buildPayload = () => {
    const resolvedFyId = financialYearId || item.financial_year_id;

    // The picker's value is a scope, not a location id: KMC-T1 has to be sent
    // as its parent location plus the sub-location.
    const { locationId: payloadLocationId, subLocationId: payloadSubLocationId } = selectedScope;

    const subToLocMap = {};
    if (payloadSubLocationId) {
      subToLocMap[payloadSubLocationId] = payloadLocationId;
    } else {
      activeSubLocations.forEach(sl => {
        if (form.sub_location_ids.includes(sl.id)) subToLocMap[sl.id] = payloadLocationId;
      });
    }

    const payloadSubLocationIds = payloadSubLocationId
      ? [payloadSubLocationId]
      : form.sub_location_ids;

    // Build form_data: { [kpi]: { [fieldKey]: value | { value, unit } } }
    const formDataPayload = {};
    kpiFormSchemas.forEach((schema) => {
      const schemaData = kpiFormData[schema.kpi] ?? {};
      // hasAnyValue: treat { value, unit } objects — only count if value is non-empty
      const hasAnyValue = Object.values(schemaData).some((v) => {
        if (v == null || v === "") return false;
        if (typeof v === "object" && "value" in v) return v.value !== "" && v.value != null;
        return true;
      });
      if (hasAnyValue) {
        const cleaned = {};
        schema.fields.forEach((field) => {
          const raw = schemaData[field.key];
          if (raw == null || raw === "") return;
          const hasUnits = Array.isArray(field.supportedUnits) && field.supportedUnits.length > 0;
          if (hasUnits && typeof raw === "object" && "value" in raw) {
            const numVal = raw.value !== "" && raw.value != null ? parseFloat(String(raw.value)) : null;
            if (numVal != null && !isNaN(numVal)) {
              cleaned[field.key] = { value: numVal, unitId: raw.unitId ?? "" };
            }
          } else if (!hasUnits) {
            cleaned[field.key] = field.type === "number" ? parseFloat(raw) : raw;
          }
        });
        if (Object.keys(cleaned).length > 0) formDataPayload[schema.kpi] = cleaned;
      }
    });
    const hasFormData = Object.keys(formDataPayload).length > 0;

    return {
      financial_year_id: resolvedFyId,
      location_ids: [payloadLocationId],
      ...(payloadSubLocationIds.length
        ? { sub_location_ids: payloadSubLocationIds, sub_location_to_location_map: subToLocMap }
        : {}),
      module: item.module,
      questionId: item.id,
      from_date: form.from_date,
      to_date: form.to_date,
      value: parseFloat(form.value),
      unit: currentUnitObjs.length > 0 ? (form.unit || null) : null,
      unit_id: currentUnitObjs.length > 0 ? (form.unit_id || null) : null,
      utility_type: selectedLocationHasConfig ? form.utility_type : "exclusive",
      ...(form.kpiAssetId ? { kpiAssetId: Number(form.kpiAssetId) } : {}),
      ...(isEmissionKpi && selectedCalcMethod ? { emission_category_id: emissionCategoryId, calculation_method: selectedCalcMethod } : {}),
      documentIds: [],
      uploadedDocuments: extraUploadedDocuments,
      shouldParse,
      ...(hasFormData ? { form_data: formDataPayload } : {}),
    };
  };

  const callSaveApi = payload =>
    apiCall(`${config.POSTLOGIN_API_URL_COMPANY}quantative-data`, {}, payload, "POST", false, false);

  const handleSave = async (isConfirmed = false, submitAfterSave = false, sendToAuditAfterSave = false) => {
    setError("");
    if (!isConfirmed) {
      if (!item) return setError("Invalid KPI item.");
      if (!form.location_id) return setError("Select a location.");
      // A sub-location scope is only valid with its parent site: without one the
      // payload would carry `location_ids: [null]`.
      if (!selectedScope.locationId) return setError("Selected location could not be resolved. Please re-select it.");
      if (!form.from_date || !form.to_date) return setError("Date range is required.");
      if (new Date(form.from_date) > new Date(form.to_date)) return setError("From date cannot be after to date.");
      if (form.value === "" || isNaN(form.value)) return setError("Value must be a valid number.");
      if (isEmissionKpi && !selectedCalcMethod) return setError("Select a calculation method for this emission KPI.");
      if (assetsLoading) return setError(`${assetKindLabel}s are still loading. Please wait a moment.`);

      // Validate KPI forms: if any field has a value, all required fields must be filled
      for (const schema of kpiFormSchemas) {
        const schemaData = kpiFormData[schema.kpi] ?? {};
        const hasAnyValue = Object.values(schemaData).some((v) => {
          if (v == null || v === "") return false;

          if (typeof v === "object" && "value" in v) return v.value !== "" && v.value != null;
          return true;
        });
        if (hasAnyValue) {
          const missingRequired = schema.fields.filter((f) => {
            if (!f.required) return false;
            const val = schemaData[f.key];
            if (val == null || val === "") return true;
            if (typeof val === "object" && "value" in val) return val.value === "" || val.value == null;
            return false;
          });
          if (missingRequired.length > 0) {
            return setError(
              `${schema.kpi}: Please fill required fields: ${missingRequired.map((f) => f.field).join(", ")}`
            );
          }
        }
      }

      const resolvedFyId = financialYearId || item.financial_year_id;
      if (!resolvedFyId) return setError("Financial year could not be determined. Please refresh.");
      setSubmitOnConfirm(submitAfterSave);
      setSendToAuditOnConfirm(sendToAuditAfterSave);
    }
    const payload = isConfirmed && conflictPayload
      ? { ...conflictPayload, confirmOverwrite: true }
      : buildPayload();
    setSaving(true);
    try {
      const result = await callSaveApi(payload);
      if (result?.status === 409 || result?.statusCode === 409) {
        setConflictPayload(payload);
        setStep("conflict");
        return;
      }
      if (!result?.isSuccess) {
        setError(result?.message || "Failed to save data. Please check your inputs.");
        if (step === "conflict") setStep("form");
        return;
      }

      const actualSubmit = isConfirmed ? submitOnConfirm : submitAfterSave;
      const actualSendToAudit = isConfirmed ? sendToAuditOnConfirm : sendToAuditAfterSave;
      if (actualSubmit && result?.data?.data?.id) {
        const recordId = result.data.data.id;
        const submitRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${recordId}/submit-for-review`,
          {},
          {},
          "PATCH"
        );
        if (!submitRes?.isSuccess) {
          console.warn("Save succeeded but submit-for-review failed:", submitRes?.message);
        }
      } else if (actualSendToAudit && result?.data?.data?.id) {
        const recordId = result.data.data.id;
        const sendRes = await apiCall(
          `${config.POSTLOGIN_API_URL_COMPANY}quantative-data/${recordId}/send-to-audit`,
          {},
          { questionType: 'quantitative' },
          "PATCH"
        );
        if (!sendRes?.isSuccess) {
          console.warn("Save succeeded but send-to-audit failed:", sendRes?.message);
        }
      }

      onSave?.(item, payload);
      onHide();
    } catch {
      setError("A network error occurred. Please try again.");
      if (step === "conflict") setStep("form");
    } finally {
      setSaving(false);
    }
  };

  if (!show) return null;

  const stepIndex = step === "form" || step === "conflict" ? 1 : 0;

  const subLocSummary = form.sub_location_ids.length
    ? form.sub_location_ids.length === activeSubLocations.length
      ? "All sub-locations"
      : form.sub_location_ids.map(id => {
        const sl = activeSubLocations.find(s => s.id === id);
        return sl ? `${selectedLocObj?.name}-${sl.subLocation}` : null;
      }).filter(Boolean).join(", ")
    : null;


  const selectedAssetSubLabel = selectedAsset?.subLocationId
    ? activeSubLocations.find(sl => String(sl.id) === String(selectedAsset.subLocationId))?.subLocation
    : "";

  const availableMethodIds = configuredMethods.length > 0
    ? configuredMethods
    : ALL_CALC_METHODS.map(m => m.id);

  const resolvedFyId = financialYearId || item?.financial_year_id;

  return (
    <Modal show={show} onClose={onHide} maxWidth={540}>
      <ModalHeader emoji="➕" title="Add Data" subtitle={item?.kpi_name} onClose={onHide} />

      {!hasPermission("quantitative-data.create") && !hasPermission("quantitative-data.update") && userCapabilities.length > 0 ? (
        <div style={{ padding: 40, textAlign: "center" }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>🚫</div>
          <div style={{ fontSize: 18, fontWeight: 700, color: "var(--ink)", marginBottom: 8 }}>No Permission</div>
          <div style={{ fontSize: 14, color: "var(--ink-4)" }}>You do not have permission to add or edit data.</div>
        </div>
      ) : (
        <>
          {/* Required evidences — always visible, full-width, right under the header */}
          {step === "choose" && (
            <RequiredEvidences requiredEvidences={requiredEvidences} />
          )}

          {/* Step indicator */}
          <div style={{ display: "flex", alignItems: "center", gap: 0, padding: "16px 24px 14px" }}>
            {["Upload", "Fill & Save"].map((label, i) => {
              const active = i === stepIndex;
              const done = i < stepIndex;
              return (
                <div key={label} style={{ display: "flex", alignItems: "center", flex: i === 1 ? 1 : "none" }}>
                  <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
                    <div style={{ width: 22, height: 22, borderRadius: "50%", fontSize: 10, fontWeight: 800, fontFamily: "var(--font-mono)", display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", background: done ? "var(--green)" : active ? "var(--accent)" : "var(--rule)", transition: "background 0.2s" }}>
                      {done ? "✓" : i + 1}
                    </div>
                    <span style={{ fontSize: 9, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)", color: active ? "var(--accent)" : "var(--ink-4)" }}>{label}</span>
                  </div>
                  {i === 0 && <div style={{ width: 40, height: 2, borderRadius: 1, margin: "0 8px 14px", background: done ? "var(--green)" : "var(--rule)", transition: "background 0.2s" }} />}
                </div>
              );
            })}
          </div>

          <div style={{ padding: "0 24px 24px" }}>

            {/* ══ EVIDENCE ══ one persistent instance so uploaded files survive
                the Upload -> Fill & Save transition; only its dropzone
                visibility (and the control below it) changes with the step. */}
            {step !== "conflict" && (
              <div style={{ marginBottom: 16 }}>
                <FieldLabel>Evidence</FieldLabel>
                <MultiDocumentUploadField
                  existingDocuments={[]}
                  resetKey="new-entry"
                  onChange={({ uploadedDocuments, isUploading }) => { setExtraUploadedDocuments(uploadedDocuments); setDocsUploading(isUploading); }}
                  hideDropzone={step !== "choose"}
                />
                {extraUploadedDocuments.length > 0 && (
                  <label style={{ display: "flex", alignItems: "center", gap: 7, marginTop: 12, fontSize: 12, color: "var(--ink-3)", cursor: "pointer" }}>
                    <input type="checkbox" checked={shouldParse} onChange={(e) => setShouldParse(e.target.checked)} />
                    Parse uploaded document(s) with AI
                  </label>
                )}
                {step === "choose" ? (
                  <SecondaryButton onClick={() => setStep("form")} style={{ width: "100%", marginTop: 14, padding: "10px 0", fontSize: 13 }}>
                    {extraUploadedDocuments.length > 0 ? "Continue →" : "Continue without uploading"}
                  </SecondaryButton>
                ) : (
                  <button onClick={() => setStep("choose")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: "var(--accent)", fontWeight: 700, padding: 0, marginTop: 10, fontFamily: "var(--font-sans)" }}>
                    {extraUploadedDocuments.length > 0 ? "← Add more documents" : "← Add documents"}
                  </button>
                )}
              </div>
            )}

            {/* ══ CONFLICT ══ */}
            {step === "conflict" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 20, textAlign: "center", padding: "20px 0" }}>
                <div style={{ fontSize: 52 }}>⚠️</div>
                <div>
                  <h3 style={{ margin: "0 0 10px", fontSize: 18, color: "var(--ink)" }}>Data Already Exists</h3>
                  <p style={{ fontSize: 13, color: "var(--ink-3)", lineHeight: "1.6", margin: 0 }}>
                    A record already exists for the selected location within this date range. You can go back and adjust your inputs, or save this new record alongside the existing one.
                  </p>
                </div>
                <div style={{ background: "var(--surface-2)", padding: "12px 16px", borderRadius: 10, fontSize: 12, textAlign: "left", border: "1px solid var(--rule)", lineHeight: "1.8" }}>
                  <div style={{ fontWeight: 700, marginBottom: 6, color: "var(--ink-2)", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.06em", fontFamily: "var(--font-mono)" }}>Summary</div>
                  <div>📍 {allowedLocations.find(l => l.id === form.location_id)?.name || "Location"}</div>
                  {subLocSummary && <div>🏢 Sub-locations: {subLocSummary}</div>}
                  {selectedAsset && <div>🏭 {assetKindLabel}: {selectedAsset.assetTitle}</div>}
                  <div>📅 {form.from_date} → {form.to_date}</div>
                  <div>📊 {form.value} {form.unit}</div>
                  {selectedLocationHasConfig && (
                    <div>🔌 Utility: {form.utility_type === "shared" ? "Shared" : "Exclusive"}</div>
                  )}
                  {isEmissionKpi && selectedCalcMethod && (
                    <div>🧮 Method: {ALL_CALC_METHODS.find(m => m.id === selectedCalcMethod)?.label || selectedCalcMethod}</div>
                  )}
                </div>
                {error && (
                  <div style={{ display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600, alignItems: "center", textAlign: "left" }}>
                    ⚠️ {error}
                  </div>
                )}
                <div style={{ display: "flex", gap: 12 }}>
                  <SecondaryButton style={{ flex: 1 }} disabled={saving} onClick={() => { setError(""); setStep("form"); }}>← Back to Edit</SecondaryButton>
                  <PrimaryButton style={{ flex: 1, background: "var(--red)", borderColor: "var(--red)" }} disabled={saving} onClick={() => handleSave(true)}>
                    {saving
                      ? <span style={{ display: "flex", alignItems: "center", gap: 6, justifyContent: "center" }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Saving…</span>
                      : "Yes, Save Anyway"}
                  </PrimaryButton>
                </div>
              </div>
            )}

            {/* ══ FORM ══ */}
            {step === "form" && (
              <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

                {/* EMISSION METHOD */}
                {isEmissionKpi && (
                  <div style={{ padding: "14px 16px", background: "#f8fafc", border: "1.5px solid #e2e8f0", borderRadius: 10 }}>
                    {!emissionSettingsLoaded ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-4)" }}>
                        <span style={{ width: 12, height: 12, border: "2px solid #e2e8f0", borderTopColor: "#1abc9c", borderRadius: "50%", display: "inline-block", animation: "kpiSpin 0.7s linear infinite" }} />
                        Loading calculation methods…
                      </div>
                    ) : (
                      <EmissionMethodPicker
                        availableMethods={availableMethodIds}
                        selected={selectedCalcMethod}
                        onChange={setSelectedCalcMethod}
                        categoryName={emissionCategoryName}
                        scopeName={emissionScopeName}
                      />
                    )}
                  </div>
                )}


                {/* ── LOCATION (single-select; ALL locations shown) ── */}
                <div ref={locRef}>
                  <FieldLabel required>Location</FieldLabel>
                  <div
                    className="kpi-input"
                    style={{ cursor: "pointer", position: "relative", userSelect: "none" }}
                    onClick={() => {
                      setOpenLocDD(p => !p);
                      setLocSearch(""); // Reset search when opening/closing
                    }}
                  >
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                      <span style={{ fontSize: 13, color: form.location_id ? "var(--ink)" : "var(--ink-4)" }}>
                        {locLabel()}
                      </span>
                      <span style={{ fontSize: 10, color: "var(--ink-4)" }}>▾</span>
                    </div>

                    {openLocDD && (
                      <div
                        style={{ position: "absolute", top: "110%", left: 0, right: 0, background: "var(--surface)", border: "1.5px solid var(--rule)", borderRadius: 10, maxHeight: 280, overflowY: "hidden", zIndex: 50, display: "flex", flexDirection: "column", boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }}
                        onClick={e => e.stopPropagation()}
                      >
                        {/* --- Search Input Section --- */}
                        <div style={{ padding: "8px", borderBottom: "1px solid var(--rule)", background: "var(--surface-2)" }}>
                          <input
                            autoFocus
                            type="text"
                            placeholder="Search locations..."
                            value={locSearch}
                            onChange={(e) => setLocSearch(e.target.value)}
                            style={{
                              width: "100%",
                              padding: "6px 10px",
                              fontSize: "12px",
                              borderRadius: "6px",
                              border: "1px solid var(--rule)",
                              outline: "none",
                              fontFamily: "var(--font-sans)"
                            }}
                          />
                        </div>

                        {/* --- Scrollable List Section --- */}
                        <div style={{ overflowY: "auto", padding: 8, maxHeight: 220 }}>
                          {filteredLocations.length === 0 && (
                            <div style={{ padding: "20px 8px", fontSize: 12, color: "var(--ink-4)", textAlign: "center" }}>
                              {allowedLocations.length === 0 ? "No locations available" : "No matches found"}
                            </div>
                          )}

                          {filteredLocations.map(l => {
                            const isSelected = form.location_id === l.id;
                            const hasConfig = !!ownershipControlMap[l.id];
                            return (
                              <div
                                key={l.id}
                                onClick={() => selectLoc(l.id)}
                                style={{
                                  display: "flex", gap: 8, padding: "7px 8px", fontSize: 12,
                                  cursor: "pointer", borderRadius: 6, alignItems: "center",
                                  background: isSelected ? "var(--accent-3)" : "transparent",
                                }}
                              >
                                {/* Radio indicator */}
                                <span style={{
                                  width: 14, height: 14, borderRadius: "50%", flexShrink: 0,
                                  display: "inline-flex", alignItems: "center", justifyContent: "center",
                                  border: `2px solid ${isSelected ? "var(--accent)" : "var(--rule)"}`,
                                  background: isSelected ? "var(--accent)" : "transparent",
                                }}>
                                  {isSelected && <span style={{ width: 5, height: 5, borderRadius: "50%", background: "#fff", display: "inline-block" }} />}
                                </span>

                                <span style={{
                                  flex: 1,
                                  fontWeight: isSelected ? 700 : 400,
                                  color: isSelected ? "var(--accent)" : "var(--ink)",
                                }}>
                                  {l.name}
                                </span>

                                <div style={{ display: "flex", gap: 4, alignItems: "center", flexShrink: 0 }}>
                                  {hasConfig && (
                                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "#e0f2fe", color: "#0369a1", fontFamily: "var(--font-mono)" }}>
                                      ownership
                                    </span>
                                  )}
                                  {l.location?.subLocation?.length > 0 && (
                                    <span style={{ fontSize: 9, fontWeight: 700, padding: "1px 5px", borderRadius: 4, background: "var(--accent-3)", color: "var(--accent)", fontFamily: "var(--font-mono)" }}>
                                      {l.location.subLocation.length} sub
                                    </span>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                </div>

                {/* ── ASSET / SUB-KPI (optional) ── */}
                {hasKpiAssets && assetOptionsForLocation.length > 0 && (
                  <div>
                    <FieldLabel>{assetKindLabel}</FieldLabel>
                    {assetsLoading ? (
                      <div className="kpi-input" style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--ink-4)", fontSize: 12 }}>
                        <span style={{ width: 12, height: 12, border: "2px solid #e2e8f0", borderTopColor: "var(--accent)", borderRadius: "50%", display: "inline-block", animation: "kpiSpin 0.7s linear infinite" }} />
                        Loading {assetKindLabel.toLowerCase()}s…
                      </div>
                    ) : (
                      <>
                        <SimpleDropdown
                          value={form.kpiAssetId || ""}
                          onChange={selectAsset}
                          placeholder={`Select ${assetKindLabel.toLowerCase()}`}
                          minWidth="100%"
                          style={{ width: "100%" }}
                          options={assetOptionsForLocation.map(asset => {
                            const isGlobal = asset.isLocationRequired === false;
                            const sub = asset.subLocationId
                              ? activeSubLocations.find(sl => String(sl.id) === String(asset.subLocationId))?.subLocation
                              : "";
                            const suffix = isGlobal ? "" : sub ? ` - ${sub}` : "";
                            return {
                              value: asset.id,
                              label: `${asset.assetTitle}${suffix}`,
                            };
                          })}
                        />
                        {selectedAsset && (
                          <div style={{ fontSize: 10.5, color: "var(--ink-4)", marginTop: 4 }}>
                            Records for this KPI will be saved against {selectedAsset.assetTitle}
                            {selectedAsset.isLocationRequired === false
                              ? ""
                              : selectedAssetSubLabel
                                ? ` (${selectedAssetSubLabel})`
                                : ""}.
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}

                {/* SUB-LOCATION — hidden only when a selected asset already implies it */}
                {hasSubLocs && !(selectedAsset && selectedAsset.subLocationId) && (
                  <div ref={subLocRef}>
                    <FieldLabel>Sub-Location</FieldLabel>
                    <div className="kpi-input" style={{ cursor: "pointer", position: "relative", userSelect: "none" }} onClick={() => setOpenSubLocDD(p => !p)}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontSize: 13, color: form.sub_location_ids.length ? "var(--ink)" : "var(--ink-4)" }}>{subLocLabel()}</span>
                        <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                          {form.sub_location_ids.length > 0 && <span onClick={e => { e.stopPropagation(); setField("sub_location_ids", []); }} style={{ fontSize: 12, color: "var(--ink-4)", cursor: "pointer", lineHeight: 1 }} title="Clear">✕</span>}
                          <span style={{ fontSize: 10, color: "var(--ink-4)" }}>▾</span>
                        </div>
                      </div>
                      {openSubLocDD && (
                        <div style={{ position: "absolute", top: "110%", left: 0, right: 0, background: "var(--surface)", border: "1.5px solid var(--rule)", borderRadius: 10, maxHeight: 200, overflowY: "auto", zIndex: 50, padding: 8, boxShadow: "0 8px 24px rgba(0,0,0,0.12)" }} onClick={e => e.stopPropagation()}>
                          <label style={{ display: "flex", gap: 8, padding: "6px 8px", fontWeight: 700, fontSize: 12, cursor: "pointer", borderRadius: 6, alignItems: "center" }}>
                            <input type="checkbox" checked={form.sub_location_ids.length === activeSubLocations.length && activeSubLocations.length > 0} onChange={toggleAllSubLocs} />
                            Select All Sub-Locations
                          </label>
                          <div style={{ height: 1, background: "var(--rule)", margin: "4px 0" }} />
                          {activeSubLocations.map(sl => (
                            <label key={sl.id} style={{ display: "flex", gap: 8, padding: "6px 8px", fontSize: 12, cursor: "pointer", borderRadius: 6, alignItems: "center", background: form.sub_location_ids.includes(sl.id) ? "var(--accent-3)" : "transparent" }}>
                              <input type="checkbox" checked={form.sub_location_ids.includes(sl.id)} onChange={() => toggleSubLoc(sl.id)} />
                              <span style={{ flex: 1, fontWeight: form.sub_location_ids.includes(sl.id) ? 700 : 400, color: form.sub_location_ids.includes(sl.id) ? "var(--accent)" : "var(--ink)" }}>{`${selectedLocObj?.name}-${sl.subLocation}`}</span>
                              {form.sub_location_ids.includes(sl.id) && <span style={{ fontSize: 10, color: "var(--accent)" }}>✓</span>}
                            </label>
                          ))}
                        </div>
                      )}
                    </div>
                    <div style={{ fontSize: 10, color: "var(--ink-4)", marginTop: 4 }}>Optional — leave blank to apply to all sub-locations</div>
                  </div>
                )}

                {/* UTILITY TYPE */}
                {selectedLocationHasConfig && (
                  <div>
                    <FieldLabel required>Utility Type</FieldLabel>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
                      {[{ id: "exclusive", label: "Exclusive" }, { id: "shared", label: "Shared" }].map(opt => {
                        const active = form.utility_type === opt.id;
                        return (
                          <button key={opt.id} type="button" onClick={() => setField("utility_type", opt.id)} style={{ padding: "10px", borderRadius: 10, border: `2px solid ${active ? "var(--accent)" : "var(--rule)"}`, background: active ? "var(--accent-3)" : "var(--surface)", textAlign: "left", cursor: "pointer" }}>
                            <div style={{ fontSize: 13, fontWeight: 700 }}>{opt.label}</div>
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* ── SHARED USAGE DISTRIBUTION ── */}
                {selectedLocationHasConfig && form.utility_type === "shared" && form.location_id && (
                  <SharedUsageDistribution
                    locationId={selectedLocationIdForConfig}
                    locations={locations}
                    ownershipControlMap={ownershipControlMap}
                  />
                )}

                {/* DATE RANGE */}
                {periodOptions.length > 0 ? (
                  <div>
                    <FieldLabel required>Reporting Period</FieldLabel>
                    <select
                      className="kpi-input"
                      value={selectedPeriodValue}
                      onChange={e => handlePeriodChange(e.target.value)}
                      aria-label="Reporting period"
                    >
                      {!selectedPeriod && <option value={selectedPeriodValue}>Select period…</option>}
                      {periodOptions.map(option => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                    <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginTop: 4 }}>
                      {FREQUENCY_CAPTIONS[effectiveFrequency] ?? "PERIODS FROM THE KPI'S FREQUENCY"}
                      {selectedPeriod && ` · ${selectedPeriod.fromDate} → ${selectedPeriod.toDate}`}
                    </div>
                    {selectedPeriod?.partial && (
                      <div style={{ fontSize: 11, color: "var(--warn, #b45309)", marginTop: 4 }}>
                        This period has not ended yet — the figure is filed against the whole period.
                      </div>
                    )}
                  </div>
                ) : (
                  <div>
                    <FieldLabel required>Date Range</FieldLabel>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 4 }}>FROM</div>
                        <input type="date" className="kpi-input" max={today} value={form.from_date} onChange={e => setField("from_date", e.target.value)} />
                      </div>
                      <div>
                        <div style={{ fontSize: 10, color: "var(--ink-4)", fontFamily: "var(--font-mono)", marginBottom: 4 }}>TO</div>
                        <input type="date" className="kpi-input" max={today} value={form.to_date} onChange={e => setField("to_date", e.target.value)} />
                      </div>
                    </div>
                  </div>
                )}

                {/* VALUE + UNIT */}
                <div>
                  <FieldLabel required>Value</FieldLabel>
                  <div style={currentUnitObjs.length > 0 ? { display: "grid", gridTemplateColumns: "1fr 120px", gap: 8 } : {}}>
                    <input type="number" className="kpi-input" placeholder="Enter numeric value" value={form.value} onChange={e => setField("value", e.target.value)} />
                    {currentUnitObjs.length > 0 && (
                      isUnitLocked ? (
                        <div
                          className="kpi-input"
                          title="Fixed by Settings → KPI Unit Settings"
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            background: "var(--surface-2, #f4f6f8)", color: "var(--ink-2)",
                            fontWeight: 700, cursor: "default", userSelect: "none",
                          }}
                        >
                          {defaultUnit}
                        </div>
                      ) : (
                        <select className="kpi-input" value={form.unit} onChange={e => handleUnitChange(e.target.value)}>
                          {currentUnitObjs.map(u => <option key={u.unit_id} value={u.unit}>{u.unit}</option>)}
                        </select>
                      )
                    )}
                  </div>
                  {isUnitLocked && (
                    <div style={{ fontSize: 10.5, color: "var(--ink-4)", marginTop: 4 }}>
                      Unit fixed to {defaultUnit} by KPI Unit Settings — enter the value in {defaultUnit}.
                    </div>
                  )}
                  {(() => {
                    if (isUnitLocked) return null;
                    if (!form.unit || !defaultUnit || form.unit === defaultUnit) return null;
                    const num = Number(form.value);
                    if (form.value === "" || isNaN(num)) return null;
                    const chosen = currentUnitObjs.find(u => u.unit === form.unit);
                    const target = currentUnitObjs.find(u => u.unit === defaultUnit);
                    const cf = Number(chosen?.factor);
                    const tf = Number(target?.factor);
                    if (!cf || !tf || isNaN(cf) || isNaN(tf)) return null;
                    const converted = (num * cf) / tf;
                    const formatted = Number.isInteger(converted)
                      ? converted.toLocaleString()
                      : converted.toLocaleString(undefined, { maximumFractionDigits: 6 });
                    return (
                      <div style={{ fontSize: 11.5, color: "var(--ink-3)", marginTop: 6, fontWeight: 600 }}>
                        ≈ {formatted} {defaultUnit}
                      </div>
                    );
                  })()}
                  {!isUnitLocked && selectedAsset?.supportedUnits?.length > 0 && (
                    <div style={{ fontSize: 10.5, color: "var(--ink-4)", marginTop: 4 }}>
                      KPI units + {selectedAsset.assetTitle}'s own units are available.
                    </div>
                  )}
                </div>

                {/* ── KPI ADDITIONAL FORMS ── */}
                {(schemaLoading || kpiFormSchemas.length > 0) && (
                  <div style={{ borderTop: "1px solid var(--rule)", paddingTop: 14, display: "flex", flexDirection: "column", gap: 14 }}>

                    {schemaLoading ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 12, color: "var(--ink-4)", padding: "4px 0" }}>
                        <span style={{ width: 12, height: 12, border: "2px solid #e2e8f0", borderTopColor: "var(--accent)", borderRadius: "50%", display: "inline-block", animation: "kpiSpin 0.7s linear infinite" }} />
                        Loading additional fields…
                      </div>
                    ) : kpiFormSchemas.map((schema) => (
                      <div key={schema.kpi}>
                        {/* Section label */}
                        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
                          <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
                          <span style={{ fontSize: 10, fontWeight: 700, color: "var(--ink-4)", textTransform: "uppercase", letterSpacing: "0.08em", fontFamily: "var(--font-mono)", display: "flex", alignItems: "center", gap: 5 }}>
                            {schema.kpi} Data
                            <span style={{ fontSize: 9, fontWeight: 500, color: "var(--ink-5)", textTransform: "none", letterSpacing: 0 }}>(optional)</span>
                          </span>
                          <div style={{ flex: 1, height: 1, background: "var(--rule)" }} />
                        </div>

                        <KpiFormFields
                          schema={schema.fields}
                          kpiType={schema.kpi}
                          formData={kpiFormData[schema.kpi] ?? {}}
                          onChange={(key, val) =>
                            setKpiFormData((p) => ({
                              ...p,
                              [schema.kpi]: { ...(p[schema.kpi] ?? {}), [key]: val },
                            }))
                          }
                          readOnly={false}
                          variant="modal"
                          unitMaster={unitMaster}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {error && (
                  <div style={{ display: "flex", gap: 8, padding: "10px 14px", borderRadius: 8, background: "var(--red-2)", border: "1px solid #fca5a5", fontSize: 12, color: "var(--red)", fontWeight: 600, alignItems: "center" }}>
                    ⚠️ {error}
                  </div>
                )}

                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, paddingTop: 4 }}>
                  <SecondaryButton onClick={onHide}>Cancel</SecondaryButton>
                  {hasPermission("quantitative-data.send-to-audit") ? (
                    <>
                      <PrimaryButton
                        onClick={() => handleSave(false, false, false)}
                        disabled={
                          saving ||
                          docsUploading ||
                          assetsLoading ||
                          (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)
                        }
                        style={{ minWidth: 90 }}
                      >
                        {saving && !submitOnConfirm && !sendToAuditOnConfirm
                          ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Saving…</span>
                          : "Save as Draft"}
                      </PrimaryButton>
                      <PrimaryButton
                        onClick={() => handleSave(false, false, true)}
                        disabled={
                          saving ||
                          docsUploading ||
                          assetsLoading ||
                          (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)
                        }
                        style={{ minWidth: 120, background: "var(--accent)", borderColor: "var(--accent)" }}
                      >
                        {saving && sendToAuditOnConfirm
                          ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Sending…</span>
                          : "Send for Audit"}
                      </PrimaryButton>
                    </>
                  ) : hasPermission("quantitative-data.submit-for-review") ? (
                    <>
                      <PrimaryButton
                        onClick={() => handleSave(false, false, false)}
                        disabled={
                          saving ||
                          docsUploading ||
                          assetsLoading ||
                          (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)
                        }
                        style={{ minWidth: 90 }}
                      >
                        {saving && !submitOnConfirm && !sendToAuditOnConfirm
                          ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Saving…</span>
                          : "Save as Draft"}
                      </PrimaryButton>
                      <PrimaryButton
                        onClick={() => handleSave(false, true, false)}
                        disabled={
                          saving ||
                          docsUploading ||
                          assetsLoading ||
                          (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)
                        }
                        style={{ minWidth: 120, background: "var(--accent)", borderColor: "var(--accent)" }}
                      >
                        {saving && submitOnConfirm
                          ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Submitting…</span>
                          : "Submit for Review"}
                      </PrimaryButton>
                    </>
                  ) : (
                    <PrimaryButton
                      onClick={() => handleSave(false, false, false)}
                      disabled={
                        saving ||
                        docsUploading ||
                        assetsLoading ||
                        (isEmissionKpi && emissionSettingsLoaded && !selectedCalcMethod)
                      }
                      style={{ minWidth: 90 }}
                    >
                      {saving
                        ? <span style={{ display: "flex", alignItems: "center", gap: 6 }}><span style={{ width: 12, height: 12, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.4)", borderTopColor: "#fff", animation: "kpiSpin 0.7s linear infinite", display: "inline-block" }} />Saving…</span>
                        : "Save"}
                    </PrimaryButton>
                  )}
                </div>
              </div>
            )}
          </div>
        </>
      )}
    </Modal>
  );
};

export default AddDataModal;